import { useCallback, useEffect, useState } from "react";
import api, { formatApiError, getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    Download, RefreshCw, Loader2, AlertTriangle, ExternalLink, Info,
} from "lucide-react";
import { toast } from "sonner";

const TIMEFRAMES = [
    { v: "today", l: "Today" },
    { v: "yesterday", l: "Yesterday" },
    { v: "7d", l: "Last 7 Days" },
    { v: "30d", l: "Last 30 Days" },
    { v: "custom", l: "Custom" },
];

const STATUS_OPTIONS = [
    { v: "any_error", l: "Any error (failed / undelivered / any error code)" },
    { v: "failed", l: "Failed only" },
    { v: "undelivered", l: "Undelivered only" },
    { v: "all", l: "All (including delivered) — slow" },
];

const DIRECTIONS = [
    { v: "outbound-api", l: "Outbound (API)" },
    { v: "all", l: "All directions" },
];

const KNOWN_LABELS = {
    "21610": "Opted out",
    "30003": "Unreachable handset",
    "30004": "Blocked by carrier",
    "30005": "Unknown handset",
    "30006": "Landline / unreachable",
};

function StatCard({ label, value, tone, testId, sub }) {
    const toneClass = {
        rose: "border-l-4 border-rose-500 bg-rose-50/40",
        amber: "border-l-4 border-amber-500 bg-amber-50/40",
        indigo: "border-l-4 border-indigo-500 bg-indigo-50/40",
        slate: "border-l-4 border-slate-500 bg-slate-50/40",
        primary: "border-l-4 border-primary bg-primary/5",
    }[tone] || "border-l-4 border-gray-300";
    return (
        <div className={`rounded-xl p-4 border border-border shadow-sm ${toneClass}`} data-testid={testId}>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className="text-2xl font-semibold text-gray-900 mt-1">{value}</div>
            {sub ? <div className="text-xs text-muted-foreground mt-0.5">{sub}</div> : null}
        </div>
    );
}

export default function TwilioErrorExport() {
    const [timeframe, setTimeframe] = useState("7d");
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [errorCode, setErrorCode] = useState("all");
    const [status, setStatus] = useState("any_error");
    const [mgSid, setMgSid] = useState("all");
    const [fromNumber, setFromNumber] = useState("all");
    const [direction, setDirection] = useState("outbound-api");
    const [summary, setSummary] = useState(null);
    const [rows, setRows] = useState([]);
    const [rowsTotal, setRowsTotal] = useState(0);
    const [truncatedForDisplay, setTruncatedForDisplay] = useState(false);
    const [diagnostics, setDiagnostics] = useState(null);
    const [manualCode, setManualCode] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const buildParams = useCallback(() => {
        const p = { timeframe, direction, limit: 500 };
        if (timeframe === "custom") {
            if (startDate) p.start_date = startDate;
            if (endDate) p.end_date = endDate;
        }
        if (status && status !== "all") p.status = status;
        // Manual code input takes precedence over the dropdown so admins can
        // filter on codes Twilio hasn't returned in the current window (or
        // that we don't yet know the label for).
        const effectiveCode = (manualCode || "").trim() || errorCode;
        if (effectiveCode && effectiveCode !== "all") p.error_code = effectiveCode;
        if (mgSid && mgSid !== "all") p.messaging_service_sid = mgSid;
        if (fromNumber && fromNumber !== "all") p.from_number = fromNumber;
        return p;
    }, [timeframe, startDate, endDate, direction, status, errorCode, mgSid, fromNumber, manualCode]);

    const load = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const { data } = await api.get("/reports/twilio-error-export", { params: buildParams() });
            setSummary(data.summary || null);
            setRows(data.rows || []);
            setRowsTotal(data.rows_total || 0);
            setTruncatedForDisplay(!!data.rows_truncated_for_display);
            setDiagnostics(data.diagnostics || null);
            if (data.summary?.twilio_error) {
                setError(data.summary.twilio_error);
            }
        } catch (e) {
            const msg = formatApiError(e);
            toast.error(msg);
            setError(msg);
            setSummary(null); setRows([]); setRowsTotal(0);
        } finally { setLoading(false); }
    }, [buildParams]);

    useEffect(() => { load(); }, [load]);

    const downloadCSV = async () => {
        try {
            const qs = new URLSearchParams(buildParams()).toString();
            const token = getToken();
            const res = await fetch(
                `${process.env.REACT_APP_BACKEND_URL}/api/reports/twilio-error-export.csv?${qs}`,
                { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} }
            );
            if (!res.ok) throw new Error(`Export failed: ${res.status}`);
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `twilio-error-export-${timeframe}.csv`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            const wasTruncated = res.headers.get("X-Twilio-Truncated") === "1";
            toast[wasTruncated ? "warning" : "success"](
                wasTruncated
                    ? "CSV downloaded — but Twilio paginated past our safety cap. Narrow the date range for a full export."
                    : "CSV downloaded"
            );
        } catch (e) { toast.error(e.message || "CSV export failed"); }
    };

    const errorCodesSeen = summary?.error_codes_seen || [];
    const mgSidsSeen = summary?.messaging_service_sids_seen || [];

    return (
        <div className="space-y-6" data-testid="twilio-error-export-page">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h2 className="text-xl md:text-2xl font-display font-semibold tracking-tight">Twilio Error Export</h2>
                    <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
                        Direct Twilio report for outbound SMS messages with provider errors. Twilio is the source of truth;
                        CaseFlow enriches matched rows with campaign / send-job / lead / processor context. Unmatched rows
                        are still exported so nothing gets lost.
                    </p>
                    {summary?.business_timezone && (
                        <p className="text-[11px] text-muted-foreground mt-1 font-mono" data-testid="tz-note-twilio">
                            Today / Yesterday anchored to <span className="font-semibold">{summary.business_timezone}</span>.
                        </p>
                    )}
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white border border-border rounded-xl p-5 shadow-sm space-y-4" data-testid="twilio-filters">
                <div className="flex flex-wrap gap-1.5">
                    {TIMEFRAMES.map(t => (
                        <button
                            key={t.v}
                            data-testid={`twilio-tf-btn-${t.v}`}
                            onClick={() => setTimeframe(t.v)}
                            className={`px-3 py-1 text-xs rounded-full border transition ${
                                timeframe === t.v
                                    ? "bg-primary text-primary-foreground border-primary"
                                    : "bg-white text-gray-700 border-border hover:bg-gray-50"
                            }`}
                        >{t.l}</button>
                    ))}
                </div>

                {timeframe === "custom" && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <Label>Start (YYYY-MM-DD)</Label>
                            <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} data-testid="twilio-start-date" />
                        </div>
                        <div>
                            <Label>End (YYYY-MM-DD, inclusive)</Label>
                            <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} data-testid="twilio-end-date" />
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div>
                        <Label>Status</Label>
                        <Select value={status} onValueChange={setStatus}>
                            <SelectTrigger data-testid="twilio-filter-status"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {STATUS_OPTIONS.map(o => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Error Code</Label>
                        <Select value={errorCode} onValueChange={(v) => { setErrorCode(v); setManualCode(""); }}>
                            <SelectTrigger data-testid="twilio-filter-error-code"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All error codes</SelectItem>
                                {errorCodesSeen.map(e => (
                                    <SelectItem key={e.code} value={e.code}>
                                        {e.code}{e.label ? ` — ${e.label}` : ""} · {e.count}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Input
                            data-testid="twilio-manual-error-code"
                            placeholder="Or type any code (e.g. 21703, 30007)"
                            value={manualCode}
                            onChange={e => setManualCode(e.target.value.replace(/[^0-9]/g, ""))}
                            className="mt-1.5 text-xs"
                        />
                    </div>
                    <div>
                        <Label>Messaging Service SID</Label>
                        <Select value={mgSid} onValueChange={setMgSid}>
                            <SelectTrigger data-testid="twilio-filter-mg-sid"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All Messaging Services</SelectItem>
                                <SelectItem value="(blank)">(no MG SID / From-number sends)</SelectItem>
                                {mgSidsSeen.map(s => (
                                    <SelectItem key={s} value={s}>{s}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>From Number</Label>
                        <Select value={fromNumber} onValueChange={setFromNumber}>
                            <SelectTrigger data-testid="twilio-filter-from-number"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">All From numbers</SelectItem>
                                {(diagnostics?.breakdowns?.by_from_number || []).map(r => (
                                    <SelectItem key={r.key} value={r.key}>
                                        {r.key} · {r.count}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Direction</Label>
                        <Select value={direction} onValueChange={setDirection}>
                            <SelectTrigger data-testid="twilio-filter-direction"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {DIRECTIONS.map(d => <SelectItem key={d.v} value={d.v}>{d.l}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <div className="flex flex-wrap gap-2 pt-1">
                    <Button onClick={load} disabled={loading} data-testid="twilio-run">
                        {loading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1.5" />}
                        {loading ? "Loading from Twilio…" : "Refresh from Twilio"}
                    </Button>
                    <Button variant="outline" onClick={downloadCSV} disabled={!rows.length || loading} data-testid="twilio-download-csv">
                        <Download className="h-4 w-4 mr-1.5" /> Export CSV
                    </Button>
                    {summary?.twilio_pages_fetched != null && !loading && (
                        <div className="text-xs text-muted-foreground self-center ml-auto">
                            Twilio pages fetched: <span className="font-semibold">{summary.twilio_pages_fetched}</span>
                            {" · "}
                            Total scanned: <span className="font-semibold">{summary.twilio_total_fetched}</span>
                            {summary.truncated ? (
                                <span className="ml-2 text-amber-700 font-semibold">TRUNCATED — narrow the range</span>
                            ) : null}
                        </div>
                    )}
                </div>
            </div>

            {/* Error state */}
            {error && (
                <div className="border border-rose-300 bg-rose-50 rounded-xl px-4 py-3 flex items-start gap-3" data-testid="twilio-error-state">
                    <AlertTriangle className="h-4 w-4 text-rose-700 mt-0.5 shrink-0" />
                    <div className="text-sm text-rose-900">
                        <div className="font-semibold">Unable to load Twilio error report.</div>
                        <div className="mt-0.5">{error}</div>
                        <div className="text-[11px] mt-1 text-rose-800">
                            Check Twilio credentials in Settings → Providers and try again.
                        </div>
                    </div>
                </div>
            )}

            {/* Filter echo + Diagnostics — reconcile with Twilio Console */}
            {summary && !error && (
                <div className="border border-border rounded-xl bg-gray-50/50 px-4 py-3" data-testid="twilio-filter-echo">
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">
                        Applied filters (for apples-to-apples comparison with Twilio Console)
                    </div>
                    <div className="flex flex-wrap gap-2 text-[11.5px] font-mono">
                        <span className="inline-block bg-white border rounded px-2 py-0.5">
                            Range (UTC): {(summary.range_start || "").slice(0, 19)} → {(summary.range_end || "").slice(0, 19)}
                        </span>
                        <span className="inline-block bg-white border rounded px-2 py-0.5">
                            TZ: {summary.business_timezone}
                        </span>
                        <span className="inline-block bg-white border rounded px-2 py-0.5">
                            Direction: {summary.filters_applied?.direction || direction}
                        </span>
                        <span className="inline-block bg-white border rounded px-2 py-0.5">
                            Status: {summary.filters_applied?.status || status}
                        </span>
                        <span className="inline-block bg-white border rounded px-2 py-0.5">
                            Error Code: {summary.filters_applied?.error_code || "all"}
                        </span>
                        <span className="inline-block bg-white border rounded px-2 py-0.5">
                            MG SID: {summary.filters_applied?.messaging_service_sid || "all"}
                        </span>
                    </div>
                    {diagnostics && (
                        <div className="mt-2 flex flex-wrap gap-2 text-[11.5px]" data-testid="twilio-diagnostics">
                            <span className="inline-block bg-indigo-50 border border-indigo-200 rounded px-2 py-0.5 text-indigo-800">
                                Twilio raw rows: <span className="font-mono font-semibold">{diagnostics.total_raw_twilio_rows}</span>
                            </span>
                            <span className="inline-block bg-indigo-50 border border-indigo-200 rounded px-2 py-0.5 text-indigo-800">
                                Error rows: <span className="font-mono font-semibold">{diagnostics.total_raw_error_rows}</span>
                            </span>
                            <span className="inline-block bg-indigo-50 border border-indigo-200 rounded px-2 py-0.5 text-indigo-800">
                                Unique SIDs: <span className="font-mono font-semibold">{diagnostics.total_unique_twilio_sids}</span>
                            </span>
                            <span className={`inline-block rounded px-2 py-0.5 border ${
                                diagnostics.duplicate_sid_count > 0
                                    ? "bg-amber-50 border-amber-300 text-amber-900"
                                    : "bg-emerald-50 border-emerald-200 text-emerald-800"
                            }`}>
                                Duplicate SIDs collapsed: <span className="font-mono font-semibold">{diagnostics.duplicate_sid_count}</span>
                            </span>
                            <span className="inline-block bg-slate-50 border border-slate-200 rounded px-2 py-0.5 text-slate-700">
                                After filters: <span className="font-mono font-semibold">{rowsTotal}</span>
                            </span>
                        </div>
                    )}
                    <div className="text-[10.5px] text-muted-foreground mt-2">
                        Twilio&apos;s Messages API doesn&apos;t support an <code>error_code</code> query param — CaseFlow filters locally on the deduplicated SID set. Twilio Console may default to different direction / status / messaging-service filters; match them here to reconcile counts.
                    </div>
                </div>
            )}

            {/* Reconciliation Drilldown — designed to reveal why CaseFlow ≠ Twilio Console */}
            {diagnostics?.breakdowns && !error && (
                <div className="border border-border rounded-xl bg-white p-4 shadow-sm space-y-3" data-testid="twilio-reconciliation">
                    <div>
                        <div className="text-sm font-semibold">Reconciliation drilldown{errorCode && errorCode !== "all" ? ` — ${errorCode}` : ""}</div>
                        <p className="text-[11.5px] text-muted-foreground mt-0.5">
                            Universe = <span className="font-mono">{diagnostics.pre_filter_universe_size}</span> rows matching your Date + Status + Direction + Error Code — before Messaging Service / From Number narrowing.
                            To match Twilio Console, confirm the same date basis, status, direction, Messaging Service, and From Number filters here.
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {/* By Messaging Service */}
                        <div className="border border-border rounded-lg p-3">
                            <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground mb-1.5">By Messaging Service</div>
                            <div className="space-y-1">
                                {diagnostics.breakdowns.by_messaging_service_sid.map(r => (
                                    <button
                                        key={r.key}
                                        onClick={() => setMgSid(r.key)}
                                        className={`flex items-center justify-between w-full text-left text-[12px] px-2 py-0.5 rounded hover:bg-gray-50 ${mgSid === r.key ? "bg-primary/10" : ""}`}
                                        data-testid={`recon-mg-${r.key}`}
                                    >
                                        <span className="font-mono truncate mr-2" title={r.key}>{r.key.length > 20 ? r.key.slice(0, 8) + "…" + r.key.slice(-6) : r.key}</span>
                                        <span className="font-semibold">{r.count} <span className="text-muted-foreground font-normal">({r.percent}%)</span></span>
                                    </button>
                                ))}
                            </div>
                        </div>
                        {/* By From Number */}
                        <div className="border border-border rounded-lg p-3">
                            <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground mb-1.5">By From Number</div>
                            <div className="space-y-1 max-h-56 overflow-y-auto">
                                {diagnostics.breakdowns.by_from_number.map(r => (
                                    <button
                                        key={r.key}
                                        onClick={() => setFromNumber(r.key)}
                                        className={`flex items-center justify-between w-full text-left text-[12px] px-2 py-0.5 rounded hover:bg-gray-50 ${fromNumber === r.key ? "bg-primary/10" : ""}`}
                                        data-testid={`recon-from-${r.key}`}
                                    >
                                        <span className="font-mono">{r.key}</span>
                                        <span className="font-semibold">{r.count} <span className="text-muted-foreground font-normal">({r.percent}%)</span></span>
                                    </button>
                                ))}
                            </div>
                        </div>
                        {/* By Status + Direction */}
                        <div className="border border-border rounded-lg p-3 space-y-3">
                            <div>
                                <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground mb-1.5">By Status</div>
                                <div className="space-y-1">
                                    {diagnostics.breakdowns.by_status.map(r => (
                                        <div key={r.key} className="flex items-center justify-between text-[12px] px-2 py-0.5">
                                            <span className="font-mono">{r.key}</span>
                                            <span className="font-semibold">{r.count} <span className="text-muted-foreground font-normal">({r.percent}%)</span></span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground mb-1.5">By Direction</div>
                                <div className="space-y-1">
                                    {diagnostics.breakdowns.by_direction.map(r => (
                                        <div key={r.key} className="flex items-center justify-between text-[12px] px-2 py-0.5">
                                            <span className="font-mono">{r.key}</span>
                                            <span className="font-semibold">{r.count} <span className="text-muted-foreground font-normal">({r.percent}%)</span></span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Date basis comparison — three parallel counts */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {["date_created", "date_sent", "date_updated"].map(basis => {
                            const items = diagnostics.breakdowns[`by_${basis}_day`] || [];
                            const total = items.reduce((s, r) => s + r.count, 0);
                            return (
                                <div key={basis} className="border border-border rounded-lg p-3">
                                    <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground mb-1.5">
                                        By {basis.replace("_", " ")} <span className="text-gray-400">(local day)</span>
                                    </div>
                                    <div className="text-lg font-semibold" data-testid={`recon-total-${basis}`}>{total}</div>
                                    <div className="space-y-0.5 max-h-32 overflow-y-auto mt-1.5">
                                        {items.map(r => (
                                            <div key={r.key} className="flex items-center justify-between text-[11.5px]">
                                                <span className="font-mono text-gray-600">{r.key || "(unknown)"}</span>
                                                <span className="font-semibold">{r.count}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    {(mgSid !== "all" || fromNumber !== "all") && (
                        <div className="text-[11.5px] bg-primary/5 border border-primary/30 rounded px-3 py-2">
                            Filter active — click <span className="font-semibold">All Messaging Services</span> or <span className="font-semibold">All From numbers</span> in the filters row above to reset.
                        </div>
                    )}
                </div>
            )}

            {/* Summary cards */}
            {summary && (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="twilio-summary-cards">
                    <StatCard testId="twilio-card-total" label="Total Errors" value={summary.total} sub={`${summary.matched_to_caseflow} matched · ${summary.unmatched_to_caseflow} unmatched`} tone="primary" />
                    <StatCard testId="twilio-card-21610" label="21610 (Opt-Outs)" value={summary.count_21610} tone="amber" />
                    <StatCard testId="twilio-card-30003" label="30003 (Unreachable)" value={summary.count_30003} tone="rose" />
                    <StatCard testId="twilio-card-30005" label="30005 (Unknown)" value={summary.count_30005} tone="rose" />
                    <StatCard testId="twilio-card-failed" label="Failed" value={summary.count_failed} tone="rose" />
                    <StatCard testId="twilio-card-undelivered" label="Undelivered" value={summary.count_undelivered} tone="indigo" />
                    <StatCard testId="twilio-card-unmatched" label="Unmatched" value={summary.unmatched_to_caseflow} sub="No CaseFlow record" tone="slate" />
                </div>
            )}

            {/* Table */}
            <div className="bg-white border border-border rounded-xl shadow-sm overflow-hidden" data-testid="twilio-results">
                {loading ? (
                    <div className="p-10 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" /> Fetching from Twilio API…
                    </div>
                ) : rows.length === 0 ? (
                    <div className="p-12 text-center" data-testid="twilio-empty">
                        <div className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 mb-3">
                            <Info className="h-5 w-5" />
                        </div>
                        <div className="text-base font-medium text-gray-900">No Twilio error messages found for this date range.</div>
                        <p className="text-sm text-muted-foreground mt-1">Try widening the range or clearing filters.</p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        {truncatedForDisplay && (
                            <div className="px-3 py-2 text-xs bg-amber-50 border-b border-amber-200 text-amber-900">
                                Showing first {rows.length} of {rowsTotal} error rows for display. The CSV export includes all.
                            </div>
                        )}
                        <table className="w-full text-sm" data-testid="twilio-table">
                            <thead>
                                <tr className="bg-gray-50 text-gray-600 text-[11px] uppercase tracking-wide">
                                    <th className="text-left px-3 py-2 font-medium">Date Sent</th>
                                    <th className="text-left px-3 py-2 font-medium">From</th>
                                    <th className="text-left px-3 py-2 font-medium">To</th>
                                    <th className="text-left px-3 py-2 font-medium">Status</th>
                                    <th className="text-left px-3 py-2 font-medium">Error</th>
                                    <th className="text-left px-3 py-2 font-medium">MG SID</th>
                                    <th className="text-left px-3 py-2 font-medium">Message SID</th>
                                    <th className="text-left px-3 py-2 font-medium">CaseFlow</th>
                                    <th className="text-left px-3 py-2 font-medium">Campaign / Workflow</th>
                                    <th className="text-left px-3 py-2 font-medium">Contact</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-border">
                                {rows.map((r) => (
                                    <tr key={r.sid} className="hover:bg-gray-50/60" data-testid="twilio-row">
                                        <td className="px-3 py-2 align-top font-mono text-[11.5px] whitespace-nowrap">
                                            {r.date_sent || r.date_created || "—"}
                                        </td>
                                        <td className="px-3 py-2 align-top font-mono text-[11.5px]">{r.from_number || "—"}</td>
                                        <td className="px-3 py-2 align-top font-mono text-[11.5px]">{r.to_number || "—"}</td>
                                        <td className="px-3 py-2 align-top">
                                            <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium uppercase tracking-wide ring-1 ${
                                                r.status === "failed" ? "bg-rose-100 text-rose-800 ring-rose-200" :
                                                r.status === "undelivered" ? "bg-indigo-100 text-indigo-800 ring-indigo-200" :
                                                "bg-gray-100 text-gray-700 ring-gray-200"
                                            }`}>{r.status || "—"}</span>
                                        </td>
                                        <td className="px-3 py-2 align-top">
                                            {r.error_code ? (
                                                <>
                                                    <div className="font-mono text-[12px] bg-rose-50 text-rose-800 px-1.5 py-0.5 rounded inline-block">
                                                        {r.error_code}
                                                        {KNOWN_LABELS[r.error_code] ? (
                                                            <span className="text-[10px] text-rose-700 ml-1">
                                                                · {KNOWN_LABELS[r.error_code]}
                                                            </span>
                                                        ) : null}
                                                    </div>
                                                    {r.error_message ? (
                                                        <div className="text-[11px] text-gray-600 mt-0.5 max-w-xs truncate" title={r.error_message}>
                                                            {r.error_message}
                                                        </div>
                                                    ) : null}
                                                </>
                                            ) : "—"}
                                        </td>
                                        <td className="px-3 py-2 align-top font-mono text-[10.5px] text-gray-600 max-w-[130px] truncate" title={r.messaging_service_sid}>
                                            {r.messaging_service_sid || "—"}
                                        </td>
                                        <td className="px-3 py-2 align-top font-mono text-[10.5px] text-gray-600 max-w-[150px] truncate" title={r.sid}>
                                            {r.sid || "—"}
                                        </td>
                                        <td className="px-3 py-2 align-top">
                                            {r.caseflow_match_found ? (
                                                <span className="inline-flex items-center gap-1 text-[11px] bg-emerald-100 text-emerald-800 rounded px-1.5 py-0.5">
                                                    <ExternalLink className="h-3 w-3" /> {r.caseflow_source}
                                                </span>
                                            ) : (
                                                <span className="text-[11px] text-gray-400">unmatched</span>
                                            )}
                                        </td>
                                        <td className="px-3 py-2 align-top">
                                            {r.campaign_name ? (
                                                <div className="text-xs">{r.campaign_name}</div>
                                            ) : "—"}
                                            {r.template_name || r.template_key ? (
                                                <div className="text-[10px] text-gray-500 mt-0.5">{r.template_name || r.template_key}</div>
                                            ) : null}
                                        </td>
                                        <td className="px-3 py-2 align-top">
                                            {r.contact_name || "—"}
                                            {r.processor_name ? (
                                                <div className="text-[10px] text-gray-500 mt-0.5">Proc: {r.processor_name}</div>
                                            ) : null}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
